import { PaytableSection }          from '../paytableSection.class'
import { ORIENTATION }              from '../../../../constants/orientation/orientation.constant'
import { ORIENTATIONS }             from '../../../../constants/orientation/orientations.constant'
import { LANG_TEXTS }               from '../../../../constants/lang/lang-texts.constant'
import { DEFAULT_WIN_LINE_COLOURS } from '../../../../constants/defaultWinLineColours/default-win-line-colours.constant'

/**
 * @file Defines the {@link WinLinesWaysManager} class.
 * @author Joe Thompson <joe.thompson@1x2network.com>
 */

 /** Class for handling win-lines/ways in paytable */
 export class WinLinesWaysManager extends PaytableSection {

  /**
   * Object mapping colours for sections of payout
   * @memberof WinLinesWaysManager
   * @protected
   * @instance
   * @type {object}
   */
  _colours

  /**
   * Total number of win lines that fill up maximum amount of win lines allowed per row
   * @memberof WinLinesWaysManager
   * @protected
   * @instance
   * @type {object}
   */
  _fullWidthWinLines
  
  /**
   * Total amount of win lines
   * @memberof WinLinesWaysManager
   * @protected
   * @instance
   * @type {number}
   */
  _lines

  /**
   * Total number of win lines that do not fill up maximum amount of win lines allowed per row
   * @memberof WinLinesWaysManager
   * @protected
   * @instance
   * @type {number}
   */
  _notFullWidthWinLines

  /**
   * Number of win lines displaying per row
   * @memberof WinLinesWaysManager
   * @protected
   * @instance
   * @type {object}
   */
  _winLinesPerRow
     
   /**
    * Initialize win lines/ways manager
    * @param {object}      config_ - Win lines/ways section config
    * @param {HTMLElement} page_   - Win lines/ways page node
    */ 
   constructor(config_, page_) {

      super(config_, page_) //Call parent constructor

      //Create div to store win lines/ways header and add to page
      let header = document.createElement('div')
      header.classList.add('paytable-header')
      ReelConfig.winningcombinations ? header.innerHTML = Translate.do('Win Ways') : header.innerHTML = Translate.do('Win Lines') //TODO: win lines translation
      this._page.appendChild(header)

      ReelConfig.winningcombinations ? this.winways() : this.winlines() //Generate content based on game is win lines/ways

   }

   /** Generate win lines content */
   winlines() {

    //Create empty objects for landscape & portrait win lines configs
    this._winLinesPerRow       = Object.create(null)
    this._fullWidthWinLines    = Object.create(null)
    this._notFullWidthWinLines = Object.create(null)

    //Store total amount of win lines
    this._lines = this._config[0].lines[1]

    this._colours = Object.create(null)

    //Loop through colour config
    for (let c in DEFAULT_WIN_LINE_COLOURS) {
      this._config[0][c] ? this._colours[c] = this._config[0][c] : this._colours[c] = DEFAULT_WIN_LINE_COLOURS[c] //Build colour config
    }

    //If mobile
    if (framework.isTouch()) {

      //Store number of win lines per row
      this._config[0].winLinesPerRow ? this._winLinesPerRow['landscape'] = this._config[0].winLinesPerRow : this._winLinesPerRow['landscape'] = 4
      this._winLinesPerRow['portrait'] = 2

    }

    //Desktop
    else {
      this._config[0].winLinesPerRow ? this._winLinesPerRow['desktop'] = this._config[0].winLinesPerRow : this._winLinesPerRow['desktop'] = 4
    }

    //Create div to store description of win line and add to page
    let winLineDescDiv = document.createElement('div')
    winLineDescDiv.classList.add('win-line-desc')
    winLineDescDiv.innerHTML = Translate.do(LANG_TEXTS['winlines'])
    this._page.appendChild(winLineDescDiv)

    //If mobile
    if (framework.isTouch()) {

      //For each orientation
      for (let i = 0; i < ORIENTATIONS.length; i++) {

        //Store orientation
        let o = ORIENTATIONS[i]
        
        //Set total number of win lines that fill up maximum amount of win lines allowed per row
        this._fullWidthWinLines[o] = (Math.floor(this._lines / this._winLinesPerRow[o]) * this._winLinesPerRow[o])

        //Set total number of win lines that do not fill up maximum amount of win lines allowed per row
        this._notFullWidthWinLines[o] = this._lines - this._fullWidthWinLines[o]

      }

    }

    //Desktop
    else {

      //Set total number of win lines that fill up maximum amount of win lines allowed per row
      this._fullWidthWinLines['desktop'] = (Math.floor(this._lines / this._winLinesPerRow['desktop']) * this._winLinesPerRow['desktop'])

      //Set total number of win lines that do not fill up maximum amount of win lines allowed per row
      this._notFullWidthWinLines['desktop'] = this._lines - this._fullWidthWinLines['desktop']

    }

    //Loop through win lines
    for (let i = 0; i < this._lines; i++) {
      this.winline(i + 1) //Generate win line
    }

    //On orientation change
    ORIENTATION.onChange.add(v_ => {
      
        let winLines = document.getElementsByClassName('win-line') //Store win lines nodes

        //Loop through win line nodes
        for (let i = 0; i < winLines.length; i++) {

          let winLine = winLines[i]             //Store current win line node
          this.winlineWidth((i+1), winLine, v_) //Style width according to orientation

        }

    })

   }

   /**
    * Generate win lines content
    * @param {number} id_ - Win line ID
    */
   winline(id_) {

    let cH = 100 / ReelConfig.numIcons //Calculate cell height
    let cW = 100 / ReelConfig.numReels //Calculate col width

    //Create div for win line
    let line = document.createElement('div')
    line.classList.add('win-line')
    this.winlineWidth(id_, line, ORIENTATION.orientation)

    //Create p for win line ID
    let p       = document.createElement('p')
    p.innerHTML = this.zeropad(id_)
    p.classList.add('win-line-id')
    p.style.color = this._colours['text_colour']

    line.appendChild(p) //Add win line ID to win line

    //Create div to store grid representing win line
    let grid = document.createElement('div')
    grid.classList.add('win-line-grid')

    let arr = framework.getSetup().getPaylineArray(id_) //Get payline array representing win line

    //Loop through win line array
    for (let i = 0; i < arr.length; i++) {

      let val = parseInt(arr[i], 10) //Store current win line value

      //Create div representing slot column
      let col = document.createElement('div')
      col.classList.add('win-line-col')
      col.style.width = `${cW}%`
      
      //Loop through number of slot rows
      for (let j = 0; j < ReelConfig.numIcons; j++) {

        //Create div for each cell/symbol position in slot
        let cell = document.createElement('div')
        cell.classList.add('win-line-cell')
        cell.style.height = `${cH}%`

        //Colour in cell/symbol position depending if in current win line or not
        j === val ? cell.style.backgroundColor = this._colours['checked_colour'] : cell.style.backgroundColor = this._colours['unchecked_colour']

        //Add to slot column
        col.appendChild(cell)

      }

      grid.appendChild(col) //Add column to grid

    }

    line.appendChild(grid)       //Add grid to win line
    this._page.appendChild(line) //Add win line to page

   }

   /**
    * Calculates width of win line div in paytable
    * @param {number} id_ - Win line ID
    * @param {HTMLElement} l_  - Win line node
    * @param {string} o_  - Current orientation
    */
   winlineWidth(id_, l_, o_) {

    //If mobile  
    if (framework.isTouch()) {
      id_ > this._fullWidthWinLines[o_] ? l_.style.width = `${(1/this._notFullWidthWinLines[o_]) * 100}%` : l_.style.width = `${100 / this._winLinesPerRow[o_]}%`
    }

    //Desktop
    else {
      id_ > this._fullWidthWinLines['desktop'] ? l_.style.width = `${(1/this._notFullWidthWinLines['desktop']) * 100}%` : l_.style.width = `${100 / this._winLinesPerRow['desktop']}%`
    }

   }

   /** Generate win ways content */
   winways() {

    //Create div to store win ways content
    let winWaysDiv = document.createElement('div')
    winWaysDiv.classList.add('win-ways')

    let symbols = this._config[0]['win_ways'] //Store config for symbol specific win ways content

    //Loop through symbol specific win ways content
    for (let i = 0; i < symbols.length; i++) {

      let s = symbols[i] //Store object config

      //If game has screenshot
      if (s.bg) {

        //Create container div for each screenshot
        let imgDiv =  document.createElement('div')
        imgDiv.classList.add('win-ways-img')

        //Calculate win ways image width based on orientation
        this.winwaysImgWidth(symbols, imgDiv, ORIENTATION.orientation)

        //Create img for screenshot
        let img = document.createElement('img')
        img.src = s.bg
        
        //Once img loaded
        img.onload = (() => {

          //Decrease image size by half of the original
          img.style.height = `${img.height / 2}px`
          img.style.width  = `${img.width / 2}px`

        })

        imgDiv.appendChild(img)        //Add img to container
        winWaysDiv.appendChild(imgDiv) //Add container to win ways div

      }

    }

    //Loop through symbol specific win ways content
    for (let i = 0; i < symbols.length; i++) {

      let s = symbols[i] //Store symbol specific object config

      //If game specific text is to be displayed
      if (s.text) {

        //Create div to store game specific win ways text
        let symbTxtDiv = document.createElement('div')
        symbTxtDiv.classList.add('win-ways-symb-text')

        //Loop through game specific texts
        for (let j = 0; j < s.text.value.length; j++) {

          let txt = s.text.value[j] //Store game specific text

          //Create p tag to store game specific text
          let p = document.createElement('p')

          //If mobile
          if (framework.isTouch()) {
            p.classList.add(ORIENTATION.orientation) //Apply orientation styling
          }

          p.innerHTML += Translate.do(txt) //Translate text
          symbTxtDiv.appendChild(p) //Add to game specific win ways text div

        }

        winWaysDiv.appendChild(symbTxtDiv) //Add game specific win ways text div to win ways div

      }

    }

    //Create div to for generic win ways text
    let winWaysTxt = document.createElement('div')
    winWaysTxt.classList.add('win-ways-text')
    winWaysTxt.innerHTML = Translate.do(LANG_TEXTS['winways'])

    //If mobile
    if (framework.isTouch()) {
      winWaysTxt.classList.add(ORIENTATION.orientation) //Apply orientation styling
    }

    winWaysDiv.appendChild(winWaysTxt) //Add generic win ways text to page
    this._page.appendChild(winWaysDiv) //Add win ways content to page

    //On orientation change
    ORIENTATION.onChange.add(v_ => {

      let winWaysImgs = document.getElementsByClassName('win-ways-img') //Store win ways img nodes
      let symbols     = this._config[0]['win_ways']                     //Store config for symbol specific win ways content

      //If any images in win ways section
      if (winWaysImgs.length > 0) {

        //Loop through win ways img nodes
        for (let i = 0; i < winWaysImgs.length; i++) {

          let img = winWaysImgs[i]               //Store current img node
          this.winwaysImgWidth(symbols, img, v_) //Calculate win ways image width

        }

      }

    })

   }

   /**
    * Calculates width of win ways img div in paytable
    * @param {object} c_ - Config for symbol specific win ways content
    * @param {object} i_ - Win ways img node
    * @param {string} o_ - Current Orientation
    */
   winwaysImgWidth(c_, i_, o_) {
    o_ === 'landscape' ? i_.style.width = `${100 / c_.length}%` : i_.style.width = '100%'
   }

   /**
    * Zeropad numbers less than 10
    * @param {number} v_ Value to zero pad
    */
   zeropad(v_) {
     return v_ < 10 ? `0${v_}` : `${v_}`
   }

 }